import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { apiClient } from "@/lib/api";
import { SITE_URL, SITE_BRAND } from "@/lib/seo-config";
import { TRAVAUX_TYPES } from "@/lib/travaux-types";
import {
  ArrowRight, MapPin, ShieldCheck, Lock, Camera, BadgeCheck,
  Handshake, Receipt, HardHat, Sparkles, Star, Award,
} from "lucide-react";
import CityProspectForm from "@/components/CityProspectForm";
import QuickMissionForm from "@/components/QuickMissionForm";

function buildCrossSEO(type, city, cityData) {
  const c = city;
  const cityCap = c.charAt(0).toUpperCase() + c.slice(1);
  const artisansCount = cityData?.artisans_count || 0;
  return {
    title: `${type.label} à ${cityCap} · Prix, artisans SIRET vérifiés · Batisur`,
    description: `${type.label} à ${cityCap} en toute sécurité : ${artisansCount} artisan${artisansCount > 1 ? "s" : ""} SIRET vérifié${artisansCount > 1 ? "s" : ""}, prix ${type.hero.priceRange}, paiement escrow Stripe, preuves photos horodatées. Devis gratuits en 48h.`,
    keywords: [
      `${type.label.toLowerCase()} ${cityCap}`,
      `prix ${type.label.toLowerCase()} ${cityCap}`,
      `artisan ${type.label.toLowerCase()} ${cityCap}`,
      `devis ${type.label.toLowerCase()} ${cityCap}`,
      `${type.label.toLowerCase()} ${cityCap} gratuit`,
      ...type.submetiers.map((m) => `${m.h3.split(" ")[0].toLowerCase()} ${cityCap}`),
      `${type.label.toLowerCase()} ${cityCap} paiement sécurisé`,
      `${type.label.toLowerCase()} ${cityCap} SIRET vérifié`,
      `${type.label.toLowerCase()} pas cher ${cityCap}`,
      `${type.label.toLowerCase()} près chez moi ${cityCap}`,
      `entreprise ${type.label.toLowerCase()} ${cityCap}`,
      `professionnel ${type.label.toLowerCase()} ${cityCap}`,
      `Batisur ${cityCap}`,
    ].join(", "),
    canonical: `/travaux/${type.slug}/${c}`,
    ogType: "website",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": `${type.label} à ${cityCap}`,
        "serviceType": type.label,
        "description": `Service ${type.label} sécurisé à ${cityCap} via Batisur Connect.`,
        "provider": { "@type": "Organization", "name": SITE_BRAND, "url": SITE_URL },
        "areaServed": { "@type": "City", "name": cityCap },
        "audience": { "@type": "PeopleAudience", "audienceType": "Particuliers, promoteurs, syndics, architectes" },
      },
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": `${SITE_BRAND} · ${type.label} à ${cityCap}`,
        "url": `${SITE_URL}/travaux/${type.slug}/${c}`,
        "description": `${type.label} à ${cityCap} avec artisans SIRET vérifiés Batisur. Paiement escrow Stripe.`,
        "areaServed": { "@type": "City", "name": cityCap },
        "priceRange": "€€",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Travaux", "item": `${SITE_URL}/travaux` },
          { "@type": "ListItem", "position": 3, "name": type.label, "item": `${SITE_URL}/travaux/${type.slug}` },
          { "@type": "ListItem", "position": 4, "name": cityCap, "item": `${SITE_URL}/travaux/${type.slug}/${c}` },
        ],
      },
    ],
  };
}

export default function TravauxTypeCity() {
  const { type: slug, city } = useParams();
  const type = TRAVAUX_TYPES[slug];
  const [cityData, setCityData] = useState(null);
  const [cityError, setCityError] = useState(false);

  useEffect(() => {
    if (city) {
      apiClient
        .get(`/public/cities/${city}`)
        .then(({ data }) => setCityData(data))
        .catch(() => setCityError(true));
    }
  }, [city]);

  if (!type) return <Navigate to="/travaux" replace />;
  if (cityError) return <Navigate to={`/travaux/${slug}`} replace />;

  const cityLabel = cityData?.city_label || city.charAt(0).toUpperCase() + city.slice(1);

  return (
    <div className="min-h-screen bg-[#F7F7F9]" data-testid={`travaux-city-page-${slug}-${city}`}>
      <SEO cfg={buildCrossSEO(type, city, cityData)} />
      <Navbar />

      {/* Hero */}
      <section className="bg-[#0A0A0B] text-white border-b-2 border-[#0A0A0B] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20 relative">
          <nav className="mono text-[11px] uppercase tracking-widest text-zinc-400 mb-6" aria-label="Fil d'ariane">
            <Link to="/" className="hover:text-[#FF4D00]">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/travaux" className="hover:text-[#FF4D00]">Travaux</Link>
            <span className="mx-2">/</span>
            <Link to={`/travaux/${type.slug}`} className="hover:text-[#FF4D00]">{type.label}</Link>
            <span className="mx-2">/</span>
            <span className="text-[#FF4D00]">{cityLabel}</span>
          </nav>

          <div className="grid lg:grid-cols-[1fr_360px] gap-10 items-end">
            <div>
              <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">
                // {type.hero.overline} · {cityLabel}
              </div>
              <h1 className="display text-4xl lg:text-6xl tracking-tighter mt-3 leading-[0.95]">
                {type.label} à <span className="text-[#FF4D00]">{cityLabel}</span>
              </h1>
              <p className="mt-6 text-zinc-300 text-base lg:text-lg max-w-2xl">
                {type.hero.lead.replace(/en toute sécurité|de confiance/i, `à ${cityLabel}`)}
                {cityData?.artisans_count ? ` ${cityData.artisans_count} artisan${cityData.artisans_count > 1 ? "s" : ""} SIRET vérifié${cityData.artisans_count > 1 ? "s" : ""} disponible${cityData.artisans_count > 1 ? "s" : ""} localement.` : ""}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={`/register?role=demandeur&travaux=${type.slug}&ville=${city}`} data-testid={`crosscta-mission-${slug}-${city}`}>
                  <button className="btn-batisur flex items-center gap-2">
                    Publier ma mission à {cityLabel} <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
                <Link to={`/artisans/ville/${city}`}>
                  <button className="btn-anthracite border-2 border-white/40 flex items-center gap-2">
                    Voir les artisans de {cityLabel}
                  </button>
                </Link>
              </div>
            </div>

            <div className="border-2 border-[#FF4D00] bg-[#FF4D00]/10 backdrop-blur-sm p-6">
              <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00] font-semibold">Fourchette prix {cityLabel}</div>
              <div className="display text-4xl mt-2 text-white leading-tight">{type.hero.priceRange}</div>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-400 mt-2">{type.hero.priceNote}</div>
              {cityData?.artisans_count > 0 && (
                <div className="mt-4 pt-4 border-t border-white/20 flex items-center gap-2">
                  <BadgeCheck className="w-4 h-4 text-[#FF4D00]" />
                  <span className="text-sm text-white">{cityData.artisans_count} artisan{cityData.artisans_count > 1 ? "s" : ""} SIRET à {cityLabel}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Trust pillars */}
      <section className="max-w-7xl mx-auto px-6 py-16 border-b-2 border-[#0A0A0B]">
        <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Pourquoi Batisur à {cityLabel}</div>
        <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95]">
          Les 3 protections <span className="text-[#FF4D00]">pour votre chantier à {cityLabel}.</span>
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {[
            { i: Lock, t: "Séquestre Stripe", d: `Vos fonds pour votre ${type.label.toLowerCase()} à ${cityLabel} sont bloqués chez Stripe (agréé bancaire). Libérés uniquement après validation par photos horodatées.` },
            { i: Camera, t: "Preuves horodatées", d: `Chaque étape de votre chantier à ${cityLabel} est prouvée par photo GPS + timestamp SHA-256. Recevable en justice.` },
            { i: BadgeCheck, t: "SIRET Sirene vérifié", d: `Les artisans de ${cityLabel} sont contrôlés en temps réel via la base officielle INSEE. Aucun faux professionnel.` },
          ].map(({ i: Icon, t, d }, idx) => (
            <div key={idx} className="border-2 border-[#0A0A0B] bg-white p-6 shadow-[3px_3px_0_#0A0A0B]">
              <div className="w-12 h-12 border-2 border-[#0A0A0B] bg-[#FF4D00] flex items-center justify-center mb-4">
                <Icon className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <div className="display text-lg leading-tight">{t}</div>
              <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Sub-métiers × ville */}
      <section className="bg-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Artisans à {cityLabel}</div>
          <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95]">
            Corps de métier pour {type.label.toLowerCase()} <span className="text-[#FF4D00]">à {cityLabel}.</span>
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {type.submetiers.map((m, i) => (
              <div key={i} className="border-l-4 border-[#FF4D00] pl-5 py-1">
                <h3 className="display text-xl leading-tight">{m.h3.replace(/^([A-Za-zé\s]+?)(?=\s|$)/, `$1 à ${cityLabel}`).slice(0, 100)}</h3>
                <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Price grid */}
      <section className="bg-[#F7F7F9] border-b-2 border-[#0A0A0B]">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Prix à {cityLabel} · 2026</div>
          <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-3">
            Combien ça coûte à {cityLabel} ?
          </h2>
          <p className="mt-4 text-zinc-700 max-w-2xl">
            Fourchettes basées sur les tarifs 2026 des artisans SIRET vérifiés Batisur à {cityLabel} et environs. Prix TTC pose + fournitures.
          </p>
          <div className="mt-8 border-2 border-[#0A0A0B] bg-white shadow-[6px_6px_0_#FF4D00] overflow-hidden">
            {type.priceGrid.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 sm:gap-6 px-5 py-4 ${
                  i < type.priceGrid.length - 1 ? "border-b border-zinc-200" : ""
                } ${i % 2 === 0 ? "bg-white" : "bg-[#F7F7F9]"}`}
              >
                <div className="text-sm text-zinc-800">{row.label}</div>
                <div className="mono font-semibold text-[#FF4D00] whitespace-nowrap">{row.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#FF4D00] text-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-6xl mx-auto px-6 py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-white/90 font-semibold">// {cityLabel} · Prêt à démarrer ?</div>
            <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-2 leading-tight">
              {type.label} à {cityLabel} · Devis gratuits en 48h
            </h2>
            <p className="mt-2 text-white/90 max-w-2xl">
              Recevez plusieurs devis d'artisans SIRET vérifiés de {cityLabel}. Vous choisissez, vous payez sécurisé, vous validez à chaque étape.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to={`/register?role=demandeur&travaux=${type.slug}&ville=${city}`}>
              <button className="btn-anthracite shadow-[4px_4px_0_#0A0A0B] flex items-center gap-2" data-testid={`crosscta-final-${slug}-${city}`}>
                Créer ma mission <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to={`/travaux/${type.slug}`}>
              <button className="border-2 border-white text-white px-6 py-3 font-semibold hover:bg-white hover:text-[#FF4D00] transition">
                Voir toutes les villes
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Prospect notify (only if city is prospected) */}
      {cityData?.prospected && (
        <section className="bg-[#F7F7F9] border-b-2 border-[#0A0A0B]">
          <div className="max-w-4xl mx-auto px-6 py-14">
            <CityProspectForm
              citySlug={city}
              cityLabel={cityLabel}
              context="travaux"
              travauxSlug={type.slug}
            />
          </div>
        </section>
      )}

      {/* Quick Mission Form — inline signup + mission */}
      <section className="max-w-4xl mx-auto px-6 py-16 border-b-2 border-[#0A0A0B]">
        <QuickMissionForm
          metierLabel={type.label}
          metierSlug={type.slug}
          cityLabel={cityLabel}
          citySlug={city}
          context={`travaux:${type.slug}:${city}`}
        />
      </section>

      <Footer />
    </div>
  );
}
